import { useI18n } from '@whizz/react-i18n';
import React, { useEffect, useState } from 'react';
import type { StudentTypeWithReassessment } from '../../types';
import { CollapseSection, StudentsScatterDisplay } from '../modules';
import { Alert, Loader, Tooltip } from '../../../components';
import { mapGroupReportForLastWeeks } from '../../../mapper/groupMapper';
import { getPolishedColors } from '../../../util/styled';
import type { AllColorType, GenderType } from '../../../util/types';
import styled from 'styled-components';
import StudentIcon from '../../../components/charts/student-scatter/components/StudentIcon';
import { DateTime } from 'luxon';
import { InfoButton } from '../../../global';
import { useAnimateIndex } from '../../../hooks/useAnimateIndex';
import { LAST_WEEKS, genders } from '../../../util/vars';
import type { SectionProps } from '../Home';

type Props = SectionProps;

const LastWeeksData = ({ isLoading, groupReport, open, setOpen }: Props): JSX.Element => {
  const { translate } = useI18n();

  const [ dataLastWeeks, setDataLastWeeks ] = useState<StudentTypeWithReassessment[]>([]);

  useEffect(() => {
    if (null == groupReport) {
      return;
    }

    setDataLastWeeks(mapGroupReportForLastWeeks(groupReport));
  }, [ groupReport ]);


  const NoStudentsReturned = () => <div className={'text-muted italic'}>{translate('pages.HOME.LAST_WEEKS.NO_RECORDS_RETURNED')}</div>;
  const nonReassessedStudents: typeof dataLastWeeks = [];
  const reassessedStudents: typeof dataLastWeeks = [];

  dataLastWeeks.forEach((student) => {
    const reassessmentEndDate = student.reassessmentEndDate ? DateTime.isDateTime(student.reassessmentEndDate) ? student.reassessmentEndDate : DateTime.fromISO(student.reassessmentEndDate) : null;
    if (student.hasBeenReassessed && reassessmentEndDate && DateTime.now().diff(reassessmentEndDate, 'weeks').weeks < 8) {
      reassessedStudents.push(student);
    } else {
      nonReassessedStudents.push(student);
    }
  });

  const numberOfStudentsMakingAcceleratedProgress = nonReassessedStudents.filter((student) => (student?.chartsData?.mathsAgeImprovement ?? 0) >= 8).length;
  const numberOfStudentsNotMakingAcceleratedProgress = dataLastWeeks.length - numberOfStudentsMakingAcceleratedProgress;
  const showAcceleratedProgressChartsAndInfo = !!nonReassessedStudents.length;
  const didNotKey = reassessedStudents.length ? translate('pages.HOME.YOUR_STUDENTS.RECENT_REASSESSED.DESCRIPTION', { count: reassessedStudents.length }) : translate('pages.HOME.YOUR_STUDENTS.NOT_ACCELERATED_PROGRESS.DESCRIPTION', { count: numberOfStudentsNotMakingAcceleratedProgress });

  return (
    <CollapseSection
      headline={isLoading ? '' : translate('pages.HOME.LAST_WEEKS.HEADLINE', { weeks: LAST_WEEKS }) }
      strapline={isLoading ? '' : translate('pages.HOME.LAST_WEEKS.STRAPLINE') }
      dataCy={'home_last_weeks'}
      open={open}
      setOpen={setOpen}
    >
      {isLoading
        ? (<Loader />)
        : (
          <div data-cy="lastWeeks-container">
            {dataLastWeeks.length === 0
              ? (<NoStudentsReturned />)
              : (
                <>
                  { showAcceleratedProgressChartsAndInfo ? (
                    <div className={'space-y-2'}>
                      <StyledWrapper className={'grid gap-2 grid-cols-auto-1fr items-center p-sm rounded-default'} $color={'header'}>
                        <div className={'flex items-center justify-center rounded-default p-1 bg-header text-2xl md:text-5xl font-bold'}>{numberOfStudentsMakingAcceleratedProgress}</div>
                        <div className={'flex items-center text-md md:text-2xl font-bold'}>
                          {translate('pages.HOME.YOUR_STUDENTS.ACCELERATED_PROGRESS.DESCRIPTION', { count: numberOfStudentsMakingAcceleratedProgress })}
                        </div>
                        <div>
                          <div className={'flex items-center justify-center rounded-default bg-white p-1 text-2xl md:text-5xl font-bold'}>{numberOfStudentsNotMakingAcceleratedProgress}</div>
                        </div>
                        <div className={'flex items-center text-md md:text-2xl font-bold'}>
                          {didNotKey}
                        </div>
                      </StyledWrapper>
                      <Alert disableIcon show={false} className={'my-md'}>
                        {translate('pages.HOME.YOUR_STUDENTS.GREAT_JOB.INFO')}
                      </Alert>
                    </div>) : null }
                  <div className={'space-y-8'}>
                    <div className={'pb-lg border-b'}>
                      { showAcceleratedProgressChartsAndInfo ? (
                        <StudentsScatterDisplay
                          chart={'mathsAgeImprovement'}
                          period={'last_weeks'}
                          data={nonReassessedStudents}
                          title={translate('pages.HOME.LAST_WEEKS.MATHSAGEIMPROVEMENT.CHART.TITLE')}
                          showGradientBackground
                          data-cy="lastWeeks-8-improvement"
                        />) : null }
                      { reassessedStudents.length ? <Reassessed students={reassessedStudents} /> : null }
                    </div>
                    <StudentsScatterDisplay
                      chart={'progression'}
                      period={'last_weeks'}
                      data={dataLastWeeks}
                      className={'pb-lg border-b'}
                      data-cy="lastWeeks-8-progression"
                    />
                    <StudentsScatterDisplay
                      chart={'qualityLearningTime'}
                      period={'last_weeks'}
                      data={dataLastWeeks}
                      className={'pb-lg'}
                      data-cy="lastWeeks-8-learning"
                    />
                  </div>
                </>
              )
            }
          </div>
        )
      }
    </CollapseSection>
  );
};

const Reassessed = ( { students }: { students: StudentTypeWithReassessment[] }) => {
  const { translate } = useI18n();
  const [ showInfo, setShowInfo ] = useState<boolean>(false);
  const [ animateIndex ] = useAnimateIndex(students);
  return (
    <div>
      <div className={'flex items-center'}>
        <h3 className='font-bold'>{translate('pages.HOME.YOUR_STUDENTS.RECENT_REASSESSED.TITLE')}</h3>
        <InfoButton onClick={() => setShowInfo(!showInfo)} />
      </div>
      <Alert disableIcon show={showInfo} className={'my-md'}>
        {translate('pages.HOME.YOUR_STUDENTS.RECENT_REASSESSED.INFO')}
      </Alert>
      <div className={'flex flex-wrap gap-1'}>
        {students.sort((a, b) => a.firstName < b.firstName ? -1 : a.firstName > b.firstName ? 1 : 0).map((student, index) => (
          <div className={'mt-md'} key={`reassessed-${student.id}`}>
            <Tooltip
              key={`tooltip-reassessed-${student.id}`}
              tooltipMessage={student.fullName}
            >
              <StudentIcon
                size={'lg'}
                color={genders[student.gender ?? 1].name as GenderType}
                animate={animateIndex === index}
              />
            </Tooltip>
          </div>
        ))}
      </div>
    </div>);
};

const StyledWrapper = styled('div')<{ $color: AllColorType }>`
  background: ${(props) => getPolishedColors(props).light.color};
`;

LastWeeksData.displayName = 'LastWeeksData';

export default LastWeeksData;
